import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const navItems = [
  { name: 'Projects', anchor: 'work' },
  { name: 'Other Work', anchor: 'gallery' },
  { name: 'Contact', anchor: 'contact' },
];

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true';
  });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', isDarkMode);
  }, [isDarkMode]);

  const goToSection = (anchor) => {
    setIsMenuOpen(false);
    if (location.pathname === '/') {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: anchor } });
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-paper/90 dark:bg-coal/90 backdrop-blur border-b border-line-light dark:border-line-dark">
      <div className="max-w-site mx-auto px-5 py-4 flex items-center justify-between">
        <Link to="/" className="font-display text-xl font-semibold tracking-tight text-ink dark:text-bone">
          Adam Gordon
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.name}
              onClick={() => goToSection(item.anchor)}
              className="text-sm font-medium text-muted-light dark:text-muted-dark hover:text-copper-600 dark:hover:text-copper-400 transition-colors"
            >
              {item.name}
            </button>
          ))}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            aria-label="Toggle dark mode"
            className="p-2 rounded-full text-muted-light dark:text-muted-dark hover:text-copper-600 dark:hover:text-copper-400 transition-colors"
          >
            {isDarkMode ? <Sun size={17} /> : <Moon size={17} />}
          </button>
        </nav>

        {/* Mobile controls */}
        <div className="flex items-center gap-1 md:hidden">
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            aria-label="Toggle dark mode"
            className="p-2 text-muted-light dark:text-muted-dark"
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
            className="p-2 text-ink dark:text-bone"
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden border-t border-line-light dark:border-line-dark"
          >
            <div className="flex flex-col px-5 py-2">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => goToSection(item.anchor)}
                  className="py-3 text-left font-medium text-ink dark:text-bone hover:text-copper-600 dark:hover:text-copper-400"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navigation;
